import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ListOfSectionMenu from './ListOfSectionMenu';
import { toggleModal } from '../../reducers/modalReducer';
import { useDispatch } from 'react-redux';
import KeyFeatureSeeMoreModal from '../modals/KeyFeatureSeeMoreModal';
import ModalWrapper from '../common/Modal/ModalWrapper';

const ModuleCardMenus = ({ module, onNavigateSection, menus, reportMenu }) => {
    const [selectedSeeMore, setSelectedSeeMore] = useState({});
    const [activeDynamicMenu, setActiveDynamicMenu] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    // Tracks the trigger's bounding rect so the portal can be positioned correctly
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const hoverTimeoutRef = useRef(null);
    const dispatch = useDispatch();
    let sections = module?.['sections'] || module?.['module']?.['sections'] || [];

    const handleDynamicMenuClick = (menu) => {
        if (!menu || menu.isActive === false) return;

        if (menu.actionType === 'navigate') {
            if (menu.navigateUrl) {
                window.open(menu.navigateUrl, '_blank', 'noopener,noreferrer');
            }
            return;
        }

        if (menu.actionType === 'modal') {
            setActiveDynamicMenu(menu);
            dispatch(toggleModal({ open: 'dynamicModuleMenuModal' }));
        }
    };

    const dynamicMenus = Array.isArray(module?.menusConfig) ? module.menusConfig.filter(m => m && m.isActive !== false) : [];

    const openMenu = useCallback(() => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            // Sub-menu is 240 px wide and opens at left≈98% of the 220 px dropdown,
            // so its right edge lands at roughly rect.right + 236 px.
            // If that overflows the viewport, flip the sub to open leftward instead.
            const SUB_WIDTH = 240;
            const flipSub = (rect.right + SUB_WIDTH) > window.innerWidth;
            setDropdownPos({
                top: rect.bottom + window.scrollY + 4,
                left: rect.right + window.scrollX,
                flipSub,
            });
        }
        setIsOpen(true);
    }, []);

    const closeMenu = useCallback(() => {
        hoverTimeoutRef.current = setTimeout(() => setIsOpen(false), 400);
    }, []);

    // Close on scroll / resize so the portal doesn't drift.
    // Ignore scroll events that originate inside the dropdown itself (e.g. the sections list).
    useEffect(() => {
        if (!isOpen) return;
        const close = (e) => {
            if (e.target && e.target.closest && e.target.closest('.ds-module-menu__dropdown')) return;
            setIsOpen(false);
        };
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [isOpen]);

    // The dropdown is portalled to document.body so it escapes every
    // overflow:hidden / clipping ancestor (the 200px card, the rounded dashboard card, etc.)
    const dropdown = isOpen ? ReactDOM.createPortal(
        <div
            className={`ds-module-menu__dropdown${dropdownPos.flipSub ? ' ds-module-menu__dropdown--flip-sub' : ''}`}
            style={{
                position: 'absolute',
                top: dropdownPos.top,
                left: dropdownPos.left,
                transform: 'translateX(-100%) translateY(0)',
                zIndex: 99999,
                // Override the CSS initial state (opacity:0, pointer-events:none) since
                // the portal lives in document.body and the :hover selector no longer applies
                opacity: 1,
                pointerEvents: 'auto',
            }}
            onMouseEnter={openMenu}
            onMouseLeave={closeMenu}
        >
            <div className="ds-module-menu__section ds-submenu">
                {sections.length > 0 && (menus?.includes('listOfSections') || module?.['menus']?.includes('listOfSections')) && (
                    <ListOfSectionMenu module={module} sections={sections} onNavigateSection={onNavigateSection} />
                )}
                {dynamicMenus.map((menu) => (
                    <button
                        key={menu.key || menu.label}
                        type="button"
                        onClick={() => handleDynamicMenuClick(menu)}
                        className="ds-module-menu__item df aic"
                    >
                        {menu.iconURL && (
                            <img
                                style={{ width: '16px', height: '16px', marginRight: '8px' }}
                                src={menu.iconURL}
                                alt={menu.label || 'menu-icon'}
                            />
                        )}
                        {menu.label || 'Menu'}
                    </button>
                ))}

                {reportMenu && reportMenu.length > 0 && (
                    <>
                        <div className="ds-module-menu__divider" />
                        {reportMenu.map((menu) => (
                            <button
                                key={menu.key || menu.label}
                                type="button"
                                onClick={() => handleDynamicMenuClick(menu)}
                                className="ds-module-menu__item df aic"
                            >
                                {menu.key === 'report' && (
                                    <img style={{ width: '16px', height: '16px', marginRight: '8px' }} src={require('../../../images/dashboard/icons/R.png')} alt="report" />
                                )}
                                {menu.key === 'workflowReport' && (
                                    <img style={{ width: '16px', height: '16px', marginRight: '8px' }} src={require('../../../images/dashboard/icons/WF.png')} alt="workflow-report" />
                                )}
                                {menu.label || 'Menu'}
                            </button>
                        ))}
                    </>
                )}
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <>
            <div
                className="ds-module-menu"
                onMouseEnter={openMenu}
                onMouseLeave={closeMenu}
            >
                <div ref={triggerRef} className="ds-module-card__menu">•••</div>
                {dropdown}
            </div>
            {Object.keys(selectedSeeMore).length ? <KeyFeatureSeeMoreModal selectedSeeMore={selectedSeeMore} /> : null}

            {activeDynamicMenu && activeDynamicMenu.actionType === 'modal' && (
                <ModalWrapper
                    modalType="dynamicModuleMenuModal"
                    modalCardClasses="maw900px"
                    onCloseHandler={() => setActiveDynamicMenu(null)}
                    customStyle={{ padding: '2rem' }}
                >
                    <div className="poppins-font-style w100 df fdc">
                        {activeDynamicMenu.modalTitle && (
                            <h2 className="fs1-5rem fw600 c15 mb1rem tac">
                                {activeDynamicMenu.modalTitle}
                            </h2>
                        )}
                        {activeDynamicMenu.modalImageURL && (
                            <img
                                src={activeDynamicMenu.modalImageURL}
                                alt={activeDynamicMenu.modalTitle || 'menu-modal'}
                                style={{ width: '100%', borderRadius: '12px', objectFit: 'contain' }}
                            />
                        )}
                    </div>
                </ModalWrapper>
            )}
        </>
    )
};

export default ModuleCardMenus;
